import { ReviewsData, SortOptions } from '@lib/types/api/Review';
import { MarketplaceProductReviewFilterValues } from '@lib/types/local/MarketplaceProductReviewFilterValues';
import { useCallback, useState } from 'react';
import { ReviewFormData } from 'ui-components';
import { createMarketplaceClient } from '../../../../../lib/api/usg/marketplace';
import { PAGE_SIZE } from '../constants/reviews';
import { productReviewsDataMapper } from '../mappers/productReviewsDataMapper';

export type UseReviewsParams = {
  productId: string;
  publisherSupportContact?: string;
};

const marketplaceClient = createMarketplaceClient();

export const useReviews = ({
  productId,
  publisherSupportContact,
}: UseReviewsParams) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] =
    useState<MarketplaceProductReviewFilterValues>();
  const [activeSort, setActiveSort] = useState<string>('recent');

  const getReviews = useCallback(
    async (
      page: number,
      filter?: MarketplaceProductReviewFilterValues,
      sort?: string
    ): Promise<ReviewsData | undefined> => {
      if (!productId) {
        setError('Product ID is required');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const start = (page - 1) * PAGE_SIZE;

        const response = await marketplaceClient.listReviews({
          productId,
          start,
          count: PAGE_SIZE,
          filterRating: filter,
          sort: sort as unknown as SortOptions,
        });

        const mappedReviews = response.items.map((item) => {
          const mappedReview = productReviewsDataMapper({
            ...item,
          });

          return {
            ...mappedReview,
            reviewDetails: {
              ...mappedReview.reviewDetails,
              writeReviewModal: {
                data: {
                  rating: item.rating ?? 0,
                  title: item.title ?? '',
                  review: item.body ?? '',
                },
              },
            },
          };
        });

        const reviewsData: ReviewsData = {
          reviews: mappedReviews,
          total: response.total,
          start: response.start + 1,
          count: response.count,
          currentPage:
            response.count > 0
              ? Math.floor(response.start / response.count) + 1
              : 1,
          totalPages: Math.ceil(response.total / PAGE_SIZE),
          end: response.start + mappedReviews.length,
          activeFilterValue: filter,
        };

        return reviewsData;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Reviews fetch failed:', err);
      } finally {
        setLoading(false);
      }
    },
    [productId]
  );

  // Review Actions
  const onFilter = useCallback(
    async (filter: string) => {
      const stars = Number(filter);
      const filterValue = (
        stars === 0 ? '' : filter
      ) as MarketplaceProductReviewFilterValues;

      setActiveFilter(filterValue === '' ? undefined : filterValue);

      const reviewsData = await getReviews(
        1,
        filterValue === '' ? undefined : filterValue,
        activeSort
      );

      return reviewsData;
    },
    [getReviews, activeSort]
  );

  const onSort = useCallback(
    async (sort: string) => {
      setActiveSort(sort);
      const reviewsData = await getReviews(1, activeFilter, sort);
      return reviewsData;
    },
    [getReviews, activeFilter]
  );

  const onPageChange = useCallback(
    async (page: number) => {
      return await getReviews(page, activeFilter, activeSort);
    },
    [getReviews, activeFilter, activeSort]
  );

  const onSubmitReport = useCallback(
    async (
      reviewId: string,
      productId: string,
      report: { reportReview: string }
    ) => {
      try {
        await marketplaceClient.reportReview(
          productId,
          reviewId,
          report.reportReview
        );
      } catch (error) {
        console.error('Report failed:', error);
      }
    },
    []
  );

  const onSubmitReview = useCallback(
    async (formData: ReviewFormData) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) throw new Error('Error creating review');

        setActiveFilter(undefined);
        const reviewsData = await getReviews(1, undefined, 'recent');
        return reviewsData;
      } catch (error) {
        console.error('Submit review failed:', error);
      }
    },
    [productId, getReviews]
  );

  const onDeleteReview = useCallback(
    async (reviewId: string, mock?: boolean) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), { method: 'DELETE' });
        if (!res.ok) throw new Error('Error deleting review');

        let reviewsData = await getReviews(1, activeFilter, activeSort);
        if (mock && reviewsData && Array.isArray(reviewsData.reviews)) {
          reviewsData = {
            ...reviewsData,
            reviews: reviewsData.reviews.slice(1),
          };
        }
        return reviewsData;
      } catch (error) {
        console.error('Delete review failed:', error);
      }
    },
    [productId, getReviews, activeFilter, activeSort]
  );

  const onEditReview = useCallback(
    async (reviewId: string, formData: ReviewFormData) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) throw new Error('Error editing review');

        const reviewsData = await getReviews(1, activeFilter, activeSort);
        return reviewsData;
      } catch (error) {
        console.error('Edit review failed:', error);
      }
    },
    [productId, getReviews, activeFilter, activeSort]
  );

  // TODO: These features are not going to be part of the GA launch
  const onHelpfulVotesReview = useCallback((reviewId: string) => {
    console.log('Helpful votes clicked for review:', reviewId);
  }, []);

  const onSubmitReply = useCallback(
    (reply: string, replyId?: string | undefined) => {
      console.log('Submit reply clicked:', reply, replyId);
    },
    []
  );

  const onHelpfulVotesReply = useCallback((replyId: string) => {
    console.log('Helpful votes clicked for reply:', replyId);
  }, []);

  const onEditReply = useCallback((replyId: string) => {
    console.log('Edit reply clicked for reply:', replyId);
  }, []);

  const onDeleteReply = useCallback((replyId: string) => {
    console.log('Delete reply clicked for reply:', replyId);
  }, []);

  const onEmailPublisher = useCallback(() => {
    if (
      !publisherSupportContact ||
      typeof publisherSupportContact !== 'string' ||
      publisherSupportContact.trim().length === 0
    ) {
      console.warn('Publisher support contact is not available or invalid.');
      return;
    }
    window.location.href = `mailto:${publisherSupportContact}`;
  }, [publisherSupportContact]);

  return {
    // Data
    getReviews,
    loading,
    error,
    activeFilter,
    activeSort,

    // Actions
    onFilter,
    onSort,
    onPageChange,
    onSubmitReport,
    onSubmitReview,
    onDeleteReview,
    onEditReview,
    onEmailPublisher,
    onHelpfulVotesReview,
    onSubmitReply,
    onHelpfulVotesReply,
    onEditReply,
    onDeleteReply,
  };
};
